import React, { useState, useEffect, useCallback } from 'react';
import {
  Layers,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  Edit2,
  RefreshCw,
  FlaskConical,
  MapPin,
  FileText,
  Lock,
  Clock,
  Truck,
  Package,
} from 'lucide-react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { useCompany } from '../../context/CompanyContext';
import { useRole } from '../../context/RoleContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import DefaultItemsPicker from '../../components/catalog/DefaultItemsPicker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'pest',        label: 'Pest Control' },
  { value: 'lawn',        label: 'Lawn Care' },
  { value: 'cleaning',    label: 'Cleaning' },
  { value: 'roof',        label: 'Roof' },
  { value: 'siding',      label: 'Siding' },
  { value: 'gutter',      label: 'Gutter' },
  { value: 'driveway',    label: 'Driveway' },
  { value: 'deck',        label: 'Deck' },
  { value: 'fence',       label: 'Fence' },
  { value: 'snow',        label: 'Snow Removal' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'other',       label: 'Other' },
];

// ── Empty step factory ─────────────────────────────────────────────────────────

function emptyStep(visitNumber) {
  return {
    visitNumber,
    label: '',
    products: [],
    areas: [],
    techNotes: '',
    officeNotes: '',
    estimatedDurationMin: 60,
    defaultItems: [],
  };
}

function arrToText(arr) {
  if (!arr?.length) return '';
  return arr.join('\n');
}

function textToArr(text) {
  // Preserve newlines (including trailing empty lines) while the user is typing
  // so that pressing Enter creates a visible new line. Empty lines are sanitized
  // at save-time in handleSave().
  return (text || '').split('\n');
}

function sanitizeLines(arr) {
  return (arr || []).map(s => (s || '').trim()).filter(Boolean);
}

async function getAuthHeader() {
  const token = await getAuth().currentUser?.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

// ── Step editor panel ─────────────────────────────────────────────────────────

const StepEditor = ({ step, onChange, onDelete, stepIndex, isDefault, isNew, isEditMode }) => {
  const [expanded, setExpanded] = useState(isNew || isEditMode || stepIndex === 0);

  const update = (field, value) => onChange({ ...step, [field]: value });

  return (
    <div
      className={`rounded-lg border ${
        isDefault
          ? 'border-emerald-200 bg-emerald-50/40'
          : 'border-gray-200 bg-white'
      }`}
      data-testid={`step-editor-${isDefault ? 'default' : stepIndex + 1}`}
    >
      {/* Step header */}
      <div
        className="flex items-center gap-2 px-3 py-2.5 cursor-pointer select-none"
        onClick={() => setExpanded(v => !v)}
      >
        <div
          className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
            isDefault
              ? 'bg-emerald-100 text-emerald-700'
              : 'bg-gray-100 text-gray-700'
          }`}
        >
          {isDefault ? <RefreshCw className="w-3 h-3" /> : stepIndex + 1}
        </div>

        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-gray-900 truncate block">
            {step.label || (isDefault ? 'Repeating Maintenance Step' : `Visit ${stepIndex + 1}`)}
          </span>
          {isDefault && (
            <span className="text-[10px] text-emerald-600 font-medium uppercase tracking-wide">
              Repeating · applies to all remaining visits
            </span>
          )}
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onDelete(); }}
            className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
            title="Remove step"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </div>

      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-gray-100 pt-3">
          <div>
            <Label className="text-xs text-gray-500 mb-1 block">Step Label</Label>
            <Input
              value={step.label}
              onChange={e => update('label', e.target.value)}
              placeholder={isDefault ? 'e.g. Maintenance Treatment' : `e.g. Visit ${stepIndex + 1} — Initial Treatment`}
              className="text-sm h-8"
              data-testid={`step-label-${isDefault ? 'default' : stepIndex + 1}`}
            />
          </div>

          <div>
            <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <Package className="w-3 h-3" />
              Default Items
              <span className="text-[10px] font-normal text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">
                Reusable · from Catalog
              </span>
            </Label>
            <DefaultItemsPicker
              value={Array.isArray(step.defaultItems) ? step.defaultItems : []}
              onChange={(next) => update('defaultItems', next)}
              testIdPrefix={`step-default-items-${isDefault ? 'default' : stepIndex + 1}`}
            />
            <p className="text-[10px] text-gray-400 mt-0.5">
              Selected items are snapshotted onto each generated visit for this step.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                <FlaskConical className="w-3 h-3" />
                Products / Materials
              </Label>
              <Textarea
                value={arrToText(step.products)}
                onChange={e => update('products', textToArr(e.target.value))}
                placeholder={"Termidor SC 0.06%\nTalstar P Granules"}
                rows={3}
                className="text-xs resize-none"
                data-testid={`step-products-${isDefault ? 'default' : stepIndex + 1}`}
              />
              <p className="text-[10px] text-gray-400 mt-0.5">One per line</p>
            </div>

            <div>
              <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                Areas to Treat
              </Label>
              <Textarea
                value={arrToText(step.areas)}
                onChange={e => update('areas', textToArr(e.target.value))}
                placeholder={"Foundation perimeter\nGarage door frames"}
                rows={3}
                className="text-xs resize-none"
                data-testid={`step-areas-${isDefault ? 'default' : stepIndex + 1}`}
              />
              <p className="text-[10px] text-gray-400 mt-0.5">One per line</p>
            </div>
          </div>

          <div>
            <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <FileText className="w-3 h-3" />
              Technician Instructions
            </Label>
            <Textarea
              value={step.techNotes}
              onChange={e => update('techNotes', e.target.value)}
              placeholder="Apply 3ft band up exterior walls. Check all door/window seals..."
              rows={2}
              className="text-xs resize-none"
              data-testid={`step-tech-notes-${isDefault ? 'default' : stepIndex + 1}`}
            />
          </div>

          <div>
            <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Office Notes
              <span className="text-[10px] bg-gray-100 text-gray-500 rounded px-1">Hidden from tech</span>
            </Label>
            <Textarea
              value={step.officeNotes}
              onChange={e => update('officeNotes', e.target.value)}
              placeholder="Internal note — visible to office only..."
              rows={2}
              className="text-xs resize-none"
              data-testid={`step-office-notes-${isDefault ? 'default' : stepIndex + 1}`}
            />
          </div>

          <div>
            <Label className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Estimated Duration
            </Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="5"
                max="480"
                step="5"
                value={step.estimatedDurationMin ?? 60}
                onChange={e => update('estimatedDurationMin', parseInt(e.target.value, 10) || 60)}
                className="w-20 text-sm h-8"
                data-testid={`step-duration-${isDefault ? 'default' : stepIndex + 1}`}
              />
              <span className="text-xs text-gray-500">minutes</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

const ServicePrograms = () => {
  const { companyId } = useCompany();
  const { role } = useRole();

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [saving, setSaving] = useState(false);

  // Track which steps are newly added (so StepEditor auto-expands them)
  const [newStepIndices, setNewStepIndices] = useState(new Set());

  // Form state
  const [formName, setFormName] = useState('');
  const [formCategory, setFormCategory] = useState('pest');
  const [formDescription, setFormDescription] = useState('');
  const [formSuggestedInterval, setFormSuggestedInterval] = useState('');
  // Optional season window — "MM-DD" strings, no year. Empty string === unset.
  // Used by the assignment UI to suggest `totalOccurrences` for mid-season
  // signups. The recurrence engine never reads these.
  const [formSeasonStart, setFormSeasonStart] = useState('');
  const [formSeasonEnd, setFormSeasonEnd] = useState('');
  // Two-mode default for visits booked from this program. 'route_flexible'
  // covers the typical pest/lawn use case; user can flip per-visit.
  const [formDefaultJobMode, setFormDefaultJobMode] = useState('route_flexible');
  const [steps, setSteps] = useState([emptyStep(1)]);

  const canEdit = role === 'owner' || role === 'admin';

  // ── Load programs ──────────────────────────────────────────────────────────

  const loadPrograms = useCallback(async () => {
    if (!companyId) return;
    try {
      const headers = await getAuthHeader();
      const res = await axios.get(`${API}/api/service-programs/`, {
        params: { companyId },
        headers,
      });
      setPrograms(res.data || []);
    } catch (err) {
      console.error('[ServicePrograms] load error:', err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadPrograms();
  }, [loadPrograms]);

  // ── Open create ────────────────────────────────────────────────────────────

  const handleCreate = () => {
    setEditingProgram(null);
    setFormName('');
    setFormCategory('pest');
    setFormDescription('');
    setFormSuggestedInterval('');
    setFormSeasonStart('');
    setFormSeasonEnd('');
    setSteps([emptyStep(1)]);
    setNewStepIndices(new Set([0]));
    setModalOpen(true);
  };

  // ── Open edit ──────────────────────────────────────────────────────────────

  const handleEdit = useCallback(prog => {
    if (!canEdit) return;
    setEditingProgram(prog);
    setFormName(prog.name || '');
    setFormCategory(prog.category || 'pest');
    setFormDescription(prog.description || '');
    setFormSuggestedInterval(prog.suggestedIntervalDays != null ? String(prog.suggestedIntervalDays) : '');
    setFormSeasonStart(prog.seasonStartMonthDay || '');
    setFormSeasonEnd(prog.seasonEndMonthDay || '');
    setFormDefaultJobMode(
      prog.defaultJobMode === 'appointment' ? 'appointment' : 'route_flexible'
    );
    const numbered = (prog.steps || [])
      .filter(s => s.visitNumber !== 'default')
      .sort((a, b) => a.visitNumber - b.visitNumber);
    const defaultStep = (prog.steps || []).find(s => s.visitNumber === 'default');
    setSteps([...numbered, ...(defaultStep ? [defaultStep] : [])]);
    setNewStepIndices(new Set());
    setModalOpen(true);
  }, [canEdit]);

  // ── Delete ─────────────────────────────────────────────────────────────────

  const handleDelete = async prog => {
    if (!canEdit) return;
    if (!window.confirm(`Delete "${prog.name}"? This cannot be undone.`)) return;
    try {
      const headers = await getAuthHeader();
      await axios.delete(`${API}/api/service-programs/${prog.id}`, {
        params: { companyId },
        headers,
      });
      toast.success('Program deleted');
      await loadPrograms();
    } catch (err) {
      console.error('[ServicePrograms] delete error:', err);
      toast.error('Failed to delete program');
    }
  };

  // ── Step management ────────────────────────────────────────────────────────

  const addStep = () => {
    const numbered = steps.filter(s => s.visitNumber !== 'default');
    const next = numbered.length + 1;
    const defaultIdx = steps.findIndex(s => s.visitNumber === 'default');
    let newSteps;
    let newIdx;
    if (defaultIdx >= 0) {
      newSteps = [...steps];
      newSteps.splice(defaultIdx, 0, emptyStep(next));
      newIdx = defaultIdx;
    } else {
      newSteps = [...steps, emptyStep(next)];
      newIdx = newSteps.length - 1;
    }
    setSteps(newSteps);
    setNewStepIndices(prev => new Set([...prev, newIdx]));
  };

  const addDefaultStep = () => {
    if (steps.find(s => s.visitNumber === 'default')) {
      toast.info('A repeating step already exists');
      return;
    }
    const newIdx = steps.length;
    setSteps(prev => [...prev, { ...emptyStep('default'), visitNumber: 'default', label: 'Maintenance Visit' }]);
    setNewStepIndices(prev => new Set([...prev, newIdx]));
  };

  const updateStep = (idx, updated) => {
    setSteps(prev => prev.map((s, i) => (i === idx ? updated : s)));
  };

  const deleteStep = idx => {
    setSteps(prev => {
      const next = prev.filter((_, i) => i !== idx);
      let num = 1;
      return next.map(s => s.visitNumber === 'default' ? s : { ...s, visitNumber: num++ });
    });
    setNewStepIndices(prev => {
      const next = new Set();
      prev.forEach(i => { if (i < idx) next.add(i); else if (i > idx) next.add(i - 1); });
      return next;
    });
  };

  // ── Save ───────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error('Program name is required');
      return;
    }
    if (steps.length === 0) {
      toast.error('Add at least one visit step');
      return;
    }

    setSaving(true);
    try {
      let num = 1;
      const finalSteps = steps.map(s => {
        const cleaned = {
          ...s,
          products: sanitizeLines(s.products),
          areas: sanitizeLines(s.areas),
        };
        return cleaned.visitNumber === 'default' ? cleaned : { ...cleaned, visitNumber: num++ };
      });

      const payload = {
        companyId,
        name: formName.trim(),
        category: formCategory,
        description: formDescription.trim(),
        suggestedIntervalDays: formSuggestedInterval ? parseInt(formSuggestedInterval, 10) || null : null,
        defaultJobMode: formDefaultJobMode === 'appointment' ? 'appointment' : 'route_flexible',
        seasonStartMonthDay: formSeasonStart || null,
        seasonEndMonthDay:   formSeasonEnd   || null,
        steps: finalSteps,
      };

      const headers = await getAuthHeader();
      if (editingProgram) {
        await axios.put(`${API}/api/service-programs/${editingProgram.id}`, payload, { headers });
        toast.success('Program updated');
      } else {
        await axios.post(`${API}/api/service-programs/`, payload, { headers });
        toast.success('Program created');
      }
      setModalOpen(false);
      await loadPrograms();
    } catch (err) {
      console.error('[ServicePrograms] save error:', err);
      toast.error('Failed to save program');
    } finally {
      setSaving(false);
    }
  };

  const getCategoryLabel = v => CATEGORIES.find(c => c.value === v)?.label || v;
  const numberedStepCount = p => (p.steps || []).filter(s => s.visitNumber !== 'default').length;
  const hasDefault = p => (p.steps || []).some(s => s.visitNumber === 'default');

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Service Programs</h2>
          <p className="text-sm text-gray-500 mt-1">
            Define visit-by-visit treatment protocols for recurring service accounts
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={handleCreate}
            className="text-white"
            style={{ backgroundColor: 'var(--cb-orange)' }}
            data-testid="create-program-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Program
          </Button>
        )}
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--cb-orange)] mx-auto" />
        </div>
      ) : programs.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Layers className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No service programs yet</h3>
          <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
            Create a service program to define exactly what happens at each visit of a recurring account — products, areas, and technician instructions.
          </p>
          {canEdit && (
            <Button
              onClick={handleCreate}
              className="text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Program
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Program</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Visit Sequence</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Suggested Interval</th>
                {canEdit && <th className="w-24" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {programs.map(prog => (
                <tr key={prog.id} className="hover:bg-gray-50 transition-colors" data-testid={`program-row-${prog.id}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                        <Layers className="w-4 h-4 text-emerald-600" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{prog.name}</div>
                        {prog.description && (
                          <div className="text-xs text-gray-500 truncate max-w-[240px]">{prog.description}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                      {getCategoryLabel(prog.category)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="font-medium text-gray-900">
                        {numberedStepCount(prog)} defined visit{numberedStepCount(prog) !== 1 ? 's' : ''}
                      </span>
                      {hasDefault(prog) && (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                          <RefreshCw className="w-2.5 h-2.5" />
                          + repeating
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {prog.suggestedIntervalDays ? (
                      <span className="text-sm text-gray-700 font-medium">
                        Every {prog.suggestedIntervalDays} days
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEdit(prog)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit program"
                          data-testid={`edit-program-${prog.id}`}
                        >
                          <Edit2 className="w-4 h-4 text-gray-500" />
                        </button>
                        <button
                          onClick={() => handleDelete(prog)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete program"
                          data-testid={`delete-program-${prog.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={open => !open && setModalOpen(false)}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>
              {editingProgram ? 'Edit Service Program' : 'Create Service Program'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-5 py-4 pr-1">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1">
                <Label htmlFor="prog-name">Program Name *</Label>
                <Input
                  id="prog-name"
                  value={formName}
                  onChange={e => setFormName(e.target.value)}
                  placeholder="e.g., Pest Control — Premium Protocol"
                  data-testid="program-name-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Category</Label>
                <Select value={formCategory} onValueChange={setFormCategory}>
                  <SelectTrigger data-testid="program-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="prog-desc">Description</Label>
                <Input
                  id="prog-desc"
                  value={formDescription}
                  onChange={e => setFormDescription(e.target.value)}
                  placeholder="e.g., 13-visit quarterly program"
                />
              </div>
            </div>

            {/* Suggested interval */}
            <div className="space-y-1">
              <Label className="flex items-center gap-1.5">
                Suggested Interval
                <span className="text-[10px] font-normal text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">Optional · advisory only</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={formSuggestedInterval}
                  onChange={e => setFormSuggestedInterval(e.target.value)}
                  placeholder="e.g., 21"
                  className="w-24 text-sm h-8"
                  data-testid="program-suggested-interval-input"
                />
                <span className="text-sm text-gray-500">days between visits</span>
              </div>
              <p className="text-xs text-gray-400">
                Pre-fills the interval field when this program is linked to a service plan. Operator can override.
              </p>
            </div>

            {/* Default Scheduling Mode — applied when a visit is booked from
                this program. Operator can still override per-visit. Stacked
                card layout (vs the compact segmented control used in the
                live scheduling modals) intentionally educates first-time
                program authors about what their choice means downstream. */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                How are visits normally scheduled?
                <span className="text-[10px] font-normal text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">
                  Applied at booking · overridable per-visit
                </span>
              </Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                <button
                  type="button"
                  onClick={() => setFormDefaultJobMode('appointment')}
                  className={`text-left rounded-lg border-2 p-3.5 transition-colors ${
                    formDefaultJobMode === 'appointment'
                      ? 'border-blue-400 bg-blue-50/60'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid="program-default-mode-appointment"
                >
                  <div className={`flex items-center gap-2 mb-1 text-sm font-semibold ${
                    formDefaultJobMode === 'appointment' ? 'text-blue-700' : 'text-gray-700'
                  }`}>
                    <Lock className="h-4 w-4" />
                    Fixed Appointment
                  </div>
                  <p className="text-[11px] leading-relaxed text-gray-500">
                    Customer receives a specific arrival time.
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormDefaultJobMode('route_flexible')}
                  className={`text-left rounded-lg border-2 p-3.5 transition-colors ${
                    formDefaultJobMode === 'route_flexible'
                      ? 'border-indigo-400 bg-indigo-50/60'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid="program-default-mode-route"
                >
                  <div className={`flex items-center gap-2 mb-1 text-sm font-semibold ${
                    formDefaultJobMode === 'route_flexible' ? 'text-indigo-700' : 'text-gray-700'
                  }`}>
                    <Truck className="h-4 w-4" />
                    Route Stop
                  </div>
                  <p className="text-[11px] leading-relaxed text-gray-500">
                    Customer receives a service date only. Dispatch and Build Day determine the route sequence and ETA later.
                  </p>
                </button>
              </div>
            </div>

            {/* Season window — optional. Used by assignment UI to suggest a
                `totalOccurrences` based on (start date + interval + season end).
                Stored as "MM-DD" so the same program reuses every year. */}
            <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50/60 p-3">
              <Label className="flex items-center gap-1.5">
                Season Window
                <span className="text-[10px] font-normal text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">
                  Optional · enables remaining-visit suggestion
                </span>
              </Label>
              <div className="flex items-end gap-3 flex-wrap">
                <div>
                  <Label className="text-[11px] text-gray-500 mb-1 block">Season starts (MM-DD)</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="04-01"
                    pattern="^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$"
                    value={formSeasonStart}
                    onChange={e => setFormSeasonStart(e.target.value)}
                    className="w-24 text-sm h-8"
                    data-testid="program-season-start-input"
                    maxLength={5}
                  />
                </div>
                <div>
                  <Label className="text-[11px] text-gray-500 mb-1 block">Season ends (MM-DD)</Label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="10-31"
                    pattern="^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$"
                    value={formSeasonEnd}
                    onChange={e => setFormSeasonEnd(e.target.value)}
                    className="w-24 text-sm h-8"
                    data-testid="program-season-end-input"
                    maxLength={5}
                  />
                </div>
                {(formSeasonStart || formSeasonEnd) && (
                  <button
                    type="button"
                    onClick={() => { setFormSeasonStart(''); setFormSeasonEnd(''); }}
                    className="text-[11px] text-gray-500 hover:text-gray-700 underline self-center"
                    data-testid="program-season-clear-btn"
                  >
                    clear
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-400">
                Leave blank for year-round programs. When set, the client assignment screen
                will suggest how many visits remain in the current season — operator can
                always override. Recurrence behavior is otherwise unchanged.
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold text-gray-800">Visit Sequence</Label>
                <span className="text-xs text-gray-400">
                  {steps.filter(s => s.visitNumber !== 'default').length} defined ·{' '}
                  {steps.some(s => s.visitNumber === 'default') ? 'repeating step set' : 'no repeating step'}
                </span>
              </div>
              <div className="space-y-2">
                {steps.map((step, idx) => (
                  <StepEditor
                    key={`${idx}`}
                    step={step}
                    stepIndex={idx}
                    isDefault={step.visitNumber === 'default'}
                    isNew={newStepIndices.has(idx)}
                    isEditMode={!!editingProgram}
                    onChange={updated => updateStep(idx, updated)}
                    onDelete={() => deleteStep(idx)}
                  />
                ))}
              </div>

              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={addStep}
                  className="flex items-center gap-1.5 text-xs text-gray-600 border border-dashed border-gray-300 rounded-lg px-3 py-2 hover:border-gray-400 hover:bg-gray-50 transition-colors"
                  data-testid="add-step-btn"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Visit Step
                </button>
                {!steps.some(s => s.visitNumber === 'default') && (
                  <button
                    type="button"
                    onClick={addDefaultStep}
                    className="flex items-center gap-1.5 text-xs text-emerald-600 border border-dashed border-emerald-200 rounded-lg px-3 py-2 hover:border-emerald-400 hover:bg-emerald-50 transition-colors"
                    data-testid="add-default-step-btn"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Add Repeating Step
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t flex-shrink-0">
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="text-white"
              style={{ backgroundColor: 'var(--cb-orange)' }}
              data-testid="save-program-btn"
            >
              {saving ? 'Saving…' : editingProgram ? 'Save Changes' : 'Create Program'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ServicePrograms;
